import { CompanyService } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useCompanyActivityStore } from './companyActivityStore';
import { useCompanyDocumentRulesStore } from './companyDocumentRulesStore';
import type {
  CompanyDocument,
  CreateCompanyDocumentRequest,
  UpdateCompanyDocumentRequest,
} from '@beeboard/common';

export type DocumentStoreState = {
  companyDocuments: CompanyDocument[];
};

export const useCompanyDocumentStore = defineStore('companyDocumentStore', {
  state: (): DocumentStoreState => ({
    companyDocuments: [] as CompanyDocument[],
  }),

  getters: {
    companyDocument(state) {
      return (companyDocumentId: string): CompanyDocument => {
        const doc = state.companyDocuments.find(
          (doc) => doc.id === companyDocumentId,
        );
        if (!doc) {
          throw new Error(
            `Missing companyDocument ${companyDocumentId} in companyDocument store`,
          );
        }

        return doc;
      };
    },
    hasCompanyDocument(state) {
      return (companyDocumentId: string): boolean => {
        return state.companyDocuments.some(
          (doc) => doc.id === companyDocumentId,
        );
      };
    },
  },

  actions: {
    write(doc: CompanyDocument) {
      const documents = this.$state.companyDocuments;
      const oldDoc = documents.find((d) => d.id === doc.id);
      if (oldDoc) {
        Object.assign(oldDoc, doc);
      } else {
        documents.push(doc);
      }
    },

    async loadCompanyDocuments() {
      const companyDocuments = await CompanyService.getCompanyDocuments();
      this.$patch({ companyDocuments });
    },

    async loadDocument(documentId: string) {
      const doc = await CompanyService.getCompanyDocument(documentId);
      this.write(doc);
    },

    async create(payload: CreateCompanyDocumentRequest) {
      const errors =
        useCompanyDocumentRulesStore().validateCreateCompanyDocumentRequest(
          payload,
        );
      if (errors) {
        throw errors;
      }
      const doc = await CompanyService.createCompanyDocument(payload);
      if (payload.activity_id) {
        const activity = useCompanyActivityStore().companyActivity(
          payload.activity_id,
        );
        activity.documents.push(doc);
        await useCompanyActivityStore().loadCompanyActivity(
          payload.activity_id,
        );
      }
      this.write(doc);
    },

    async update(docId: string, payload: UpdateCompanyDocumentRequest) {
      const errors =
        useCompanyDocumentRulesStore().validateUpdateCompanyDocumentRequest(
          payload,
          docId,
        );
      if (errors) {
        throw errors;
      }
      const doc = await CompanyService.updateCompanyDocument(docId, payload);
      this.write(doc);
    },

    async delete(docId: string) {
      const errors =
        useCompanyDocumentRulesStore().validateRemoveCompanyDocumentRequest(
          docId,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.deleteCompanyDocument(docId);
      const documents = this.$state.companyDocuments;
      const docIndex = documents.findIndex((d) => d.id === docId);
      if (docIndex > -1) {
        documents.splice(docIndex, 1);
      }
    },
  },
});
